import React from 'react';
import styled from 'styled-components';

const FeaturesContainer = styled.section`
  width: 100%;
  padding: ${props => props.theme.spacing.mega} 0;
  background: ${props => props.theme.colors.background.secondary};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.large};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxlarge};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.hero};
  margin-bottom: ${props => props.theme.spacing.medium};

  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.neon.cyan} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.xxlarge};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.text.secondary};
  max-width: 700px;
  margin: 0 auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.large};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${props => props.theme.colors.background.card};
  border: 1px solid ${props => props.theme.colors.border.secondary};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.xlarge};
  text-align: center;
  transition: all ${props => props.theme.transitions.medium};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${props => props.theme.colors.primary} 0%,
      ${props => props.theme.colors.neon.cyan} 100%
    );
    transform: scaleX(0);
    transition: transform ${props => props.theme.transitions.medium};
  }

  &:hover {
    transform: translateY(-8px);
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.glow};

    &::before {
      transform: scaleX(1);
    }
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto ${props => props.theme.spacing.large};
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.background.tertiary} 0%,
    ${props => props.theme.colors.background.primary} 100%
  );
  border-radius: ${props => props.theme.borderRadius.large};
  border: 2px solid ${props => props.theme.colors.border.primary};
  font-size: 2.5rem;
  transition: all ${props => props.theme.transitions.medium};

  ${FeatureCard}:hover & {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.glow};
  }
`;

const FeatureTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xlarge};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.small};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const FeatureDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.medium};
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
`;

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const FeaturesSection: React.FC = () => {
  const features: Feature[] = [
    {
      icon: '📋',
      title: 'Parameters Catalog',
      description: "Take advantage of NEURALPROMPT's flexibility to create new Midjourney's parameters and values for your prompts."
    },
    {
      icon: '✂️',
      title: 'Customized Copy/Paste Function',
      description: 'Stop indefinitely typing the imagine command over and over with every new change in your prompt; NEURALPROMPT does it for you.'
    },
    {
      icon: '📚',
      title: 'Script Repository',
      description: 'Built-in repository for saving, viewing, and re-editing scripts, with options to clone and modify existing scripts for new projects.'
    },
    {
      icon: '⚡',
      title: 'Script Editor Functionality',
      description: 'Advanced editor for creating, editing, and managing scripts compatible with Discord chats and AI tools like Midjourney, nano bannana, chatgpt'
    },
    {
      icon: '🎨',
      title: 'Intuitive User Interface',
      description: 'Intuitive User Interface, User Friendly and intuitive interface, making it accesible and easy to use so that all users can effectively start to learn how to prompt for midjourney'
    },
    {
      icon: '🔄',
      title: 'Multiplatform Compatibility',
      description: 'Designed as desktop application for both Windows and Mac, but also you can find the corresponding app in AppStore and Google play.'
    },
    {
      icon: '🚀',
      title: 'Parameter and Value Customization',
      description: 'Offers extensive customization options for scripts including parameters, and the option to create endless values for those parameters, so that you can prepare the most accurate prompt possible for being processed'
    },
    {
      icon: '✅',
      title: 'Fully compliant with midjourney',
      description: 'You can start using NeuralPrompt for midjourney right away, with the confidence that even as new versions emerge, your editor can always create new parameters, including version-specific ones.'
    }
  ];

  return (
    <FeaturesContainer id="features">
      <Container>
        <SectionHeader>
          <SectionTitle>Powerful Features</SectionTitle>
          <SectionSubtitle>
            Everything you need to create professional prompts for Midjourney
          </SectionSubtitle>
        </SectionHeader>

        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <IconWrapper>{feature.icon}</IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
    </FeaturesContainer>
  );
};

export default FeaturesSection;
